import { useState, useRef, useMemo } from "react";
import {
  AlertTriangle,
  Barcode,
  CheckCircle2,
  Columns,
  Download,
  FileSpreadsheet,
  FileText,
  PackagePlus,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Card,
  InlineNotice,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db, type CachedProduct } from "../lib/db";
import { refreshFromServer } from "../lib/data";
import {
  parseSpreadsheetFile,
  downloadTemplateExcel,
  type ParsedSpreadsheet,
} from "../lib/spreadsheet";
import { downloadText, toCsv } from "../lib/csv";
import {
  ColumnMappingModal,
  type FieldDefinition,
} from "../components/ColumnMappingModal";

interface UnitForm {
  id: string;
  name: string;
  multiplier: number;
  barcode: string;
  salePrice: number;
}

interface ProductForm {
  name: string;
  sku: string;
  barcode: string;
  baseUnit: string;
  businessVertical: string;
  category: string;
  subcategory: string;
  salePrice: number;
  openingCost: number;
  openingQty: number;
  minStockQty: number;
  trackExpiry: boolean;
  openingLotCode: string;
  openingExpiryDate: string;
  units: UnitForm[];
}

const initialForm: ProductForm = {
  name: "",
  sku: "",
  barcode: "",
  baseUnit: "cái",
  businessVertical: "",
  category: "",
  subcategory: "",
  salePrice: 0,
  openingCost: 0,
  openingQty: 0,
  minStockQty: 0,
  trackExpiry: false,
  openingLotCode: "",
  openingExpiryDate: "",
  units: [],
};

const PRODUCT_FIELDS: FieldDefinition[] = [
  { key: "sku", label: "Mã SKU / Mã hàng", required: true, hint: "Định danh duy nhất của sản phẩm" },
  { key: "name", label: "Tên sản phẩm", required: true, hint: "Tên hiển thị khi bán và nhập hàng" },
  { key: "businessVertical", label: "Ngành kinh doanh", hint: "Ví dụ: Tạp hóa & Bách hóa" },
  { key: "category", label: "Danh mục", hint: "Ví dụ: Đồ uống" },
  { key: "subcategory", label: "Danh mục con", hint: "Ví dụ: Nước ngọt có ga" },
  { key: "barcode", label: "Mã vạch đơn vị", hint: "Quét bằng đầu đọc mã vạch" },
  { key: "baseUnit", label: "Đơn vị tính cơ bản", hint: "Ví dụ: cái, lon, chai, gói (mặc định: cái)" },
  { key: "salePrice", label: "Giá bán / đơn vị", hint: "Đơn giá bán niêm yết" },
  { key: "minStockQty", label: "Mức cảnh báo tồn tối thiểu", hint: "Ngưỡng cảnh báo khi kho sắp hết" },
  { key: "trackExpiry", label: "Theo dõi HSD/Lô", hint: "Nhập 1/true/có nếu SKU cần quản lý hạn dùng" },
  { key: "unitsJson", label: "Quy cách quy đổi (JSON)", hint: 'Ví dụ: [{"name":"thùng","multiplier":24,"salePrice":240000}]' },
];

const PRODUCT_HEADERS = [
  "sku",
  "name",
  "businessVertical",
  "category",
  "subcategory",
  "barcode",
  "baseUnit",
  "salePrice",
  "minStockQty",
  "trackExpiry",
  "unitsJson",
];

interface ParsedProductRow {
  rowIndex: number;
  raw: Record<string, string>;
  sku: string;
  name: string;
  businessVertical: string | null;
  category: string | null;
  subcategory: string | null;
  barcode: string | null;
  baseUnit: string;
  salePrice: number;
  openingCost: number;
  openingQty: number;
  minStockQty: number;
  trackExpiry: boolean;
  openingLotCode: string | null;
  openingExpiryDate: string | null;
  units: unknown[];
  errors: string[];
}

function parseImportNumber(
  value: string | number | undefined,
  label: string,
  options: { integer?: boolean } = {},
): { value: number; error?: string } {
  if (value === undefined || value === null || String(value).trim() === "") {
    return { value: 0 };
  }

  const raw = String(value).trim().replace(/[\s\u00A0]/g, "");
  let normalized = raw;

  if (/^\d{1,3}(?:[.,]\d{3})+$/.test(raw)) {
    normalized = raw.replace(/[.,]/g, "");
  } else if (raw.includes(",") && !raw.includes(".")) {
    normalized = raw.replace(",", ".");
  }

  if (!/^\d+(?:\.\d+)?$/.test(normalized)) {
    return { value: 0, error: `${label} không đúng định dạng số` };
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { value: 0, error: `${label} phải là số không âm` };
  }

  if (options.integer && !Number.isInteger(parsed)) {
    return { value: 0, error: `${label} phải là số nguyên` };
  }

  return { value: parsed };
}

function parseBoolean(value: string | boolean | undefined): boolean {
  if (typeof value === "boolean") return value;
  if (!value) return false;
  const normalized = String(value).trim().toLowerCase();
  return ["1", "true", "yes", "có", "co"].includes(normalized);
}

export function ProductCreatePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "import" ? "import" : "manual";

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const products = useLiveQuery(() => db.products.orderBy("name").toArray(), [], []);

  // Form tay
  const [form, setForm] = useState<ProductForm>(initialForm);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);

  // Trạng thái Import CSV / Excel
  const [parsedSheet, setParsedSheet] = useState<ParsedSpreadsheet | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string> | null>(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [fileName, setFileName] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const onlineRequired = !auth.demo && !navigator.onLine;

  function setMode(newMode: "manual" | "import") {
    if (newMode === "import") {
      setSearchParams({ mode: "import" });
    } else {
      setSearchParams({});
    }
    setError("");
  }

  function patch<K extends keyof ProductForm>(key: K, value: ProductForm[K]) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function addUnit() {
    setForm((current) => ({
      ...current,
      units: [
        ...current.units,
        {
          id: crypto.randomUUID(),
          name: "",
          multiplier: 1,
          barcode: "",
          salePrice: 0,
        },
      ],
    }));
  }

  function patchUnit(id: string, patchValue: Partial<UnitForm>) {
    setForm((current) => ({
      ...current,
      units: current.units.map((unit) =>
        unit.id === id ? { ...unit, ...patchValue } : unit,
      ),
    }));
  }

  function removeUnit(id: string) {
    setForm((current) => ({
      ...current,
      units: current.units.filter((unit) => unit.id !== id),
    }));
  }

  // Xử lý tạo 1 sản phẩm thủ công
  async function submitManual(event: React.FormEvent) {
    event.preventDefault();
    setError("");

    if (onlineRequired) {
      setError(
        "Cần kết nối mạng để tạo sản phẩm mới. Bán hàng với sản phẩm đã có vẫn hoạt động ngoại tuyến.",
      );
      return;
    }

    if (!form.name.trim() || !form.sku.trim()) {
      setError("Cần nhập tên sản phẩm và mã SKU.");
      return;
    }

    if (
      form.salePrice < 0 ||
      form.openingCost < 0 ||
      form.openingQty < 0 ||
      form.minStockQty < 0
    ) {
      setError("Giá và số lượng không được âm.");
      return;
    }

    if (
      form.trackExpiry &&
      form.openingQty > 0 &&
      !form.openingExpiryDate
    ) {
      setError("Tồn đầu kỳ của sản phẩm theo dõi HSD cần nhập hạn sử dụng.");
      return;
    }

    if (
      form.units.some(
        (unit) => !unit.name.trim() || unit.multiplier <= 0,
      )
    ) {
      setError("Mỗi đơn vị quy đổi cần tên và hệ số lớn hơn 0.");
      return;
    }

    setBusy(true);

    const units = form.units.map((unit) => ({
      name: unit.name.trim(),
      multiplier: unit.multiplier,
      barcode: unit.barcode.trim() || null,
      salePrice: unit.salePrice > 0 ? Math.round(unit.salePrice) : null,
    }));

    try {
      if (auth.demo) {
        await db.products.add({
          id: crypto.randomUUID(),
          name: form.name.trim(),
          sku: form.sku.trim(),
          barcode: form.barcode.trim() || null,
          baseUnit: form.baseUnit.trim() || "cái",
          businessVertical: form.businessVertical.trim() || null,
          category: form.category.trim() || null,
          subcategory: form.subcategory.trim() || null,
          salePrice: Math.round(form.salePrice),
          avgCost: Math.round(form.openingCost),
          stockQty: form.openingQty,
          minStockQty: form.minStockQty,
          trackExpiry: form.trackExpiry,
          nextExpiryDate:
            form.trackExpiry && form.openingExpiryDate
              ? `${form.openingExpiryDate}T00:00:00.000Z`
              : null,
          units: units.map((unit) => ({
            ...unit,
            id: crypto.randomUUID(),
          })),
        });
      } else {
        await api("/products", {
          method: "POST",
          body: JSON.stringify({
            name: form.name.trim(),
            sku: form.sku.trim(),
            barcode: form.barcode.trim() || null,
            baseUnit: form.baseUnit.trim() || "cái",
            businessVertical: form.businessVertical.trim() || null,
            category: form.category.trim() || null,
            subcategory: form.subcategory.trim() || null,
            salePrice: Math.round(form.salePrice),
            openingCost: Math.round(form.openingCost),
            openingQty: form.openingQty,
            minStockQty: form.minStockQty,
            trackExpiry: form.trackExpiry,
            openingLotCode: form.openingLotCode.trim() || null,
            openingExpiryDate: form.openingExpiryDate || null,
            units,
          }),
        });

        await refreshFromServer();
      }

      navigate("/products", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Không thể tạo sản phẩm",
      );
    } finally {
      setBusy(false);
    }
  }

  // Tải file mẫu CSV / Excel
  function downloadCsvTemplate() {
    const csv = toCsv(PRODUCT_HEADERS, [
      {
        sku: "SP001",
        name: "Coca Cola 330ml",
        businessVertical: "Tạp hóa & Bách hóa",
        category: "Đồ uống",
        subcategory: "Nước ngọt có ga",
        barcode: "8935001234567",
        baseUnit: "lon",
        salePrice: 10000,
        minStockQty: 12,
        trackExpiry: "true",
        unitsJson:
          '[{"name":"thùng","multiplier":24,"barcode":null,"salePrice":230000}]',
      },
      {
        sku: "SP002",
        name: "Mì Hảo Hảo tôm chua cay",
        businessVertical: "Tạp hóa & Bách hóa",
        category: "Thực phẩm",
        subcategory: "Mì ăn liền",
        barcode: "8935007890123",
        baseUnit: "gói",
        salePrice: 4000,
        minStockQty: 10,
        trackExpiry: "true",
        unitsJson: '[{"name":"thùng","multiplier":30,"salePrice":115000}]',
      },
    ]);

    downloadText(
      "bledger-product-import-template.csv",
      csv,
      "text/csv;charset=utf-8",
    );
  }

  function downloadExcelTemplate() {
    downloadTemplateExcel("PRODUCTS");
  }

  // Đọc file CSV / Excel tải lên. Cùng một pipeline cho chọn tệp và kéo-thả.
  async function parseSelectedFile(file: File) {
    setError("");
    setColumnMapping(null);
    setParsedSheet(null);
    setFileName(file.name);

    if (!/\.(csv|tsv|xlsx|xls)$/i.test(file.name)) {
      setError("Chỉ hỗ trợ CSV, TSV, Excel .xlsx hoặc .xls.");
      setFileName("");
      return;
    }

    setBusy(true);
    try {
      const parsed = await parseSpreadsheetFile(file);
      if (parsed.rows.length === 0) {
        throw new Error("Tệp không có dữ liệu hàng nào.");
      }
      setParsedSheet(parsed);
      setShowMappingModal(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Không thể đọc tệp bảng tính CSV/Excel",
      );
      setParsedSheet(null);
      setFileName("");
    } finally {
      setBusy(false);
    }
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await parseSelectedFile(file);
  }

  async function handleFileDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await parseSelectedFile(file);
  }

  function clearImportFile() {
    setParsedSheet(null);
    setColumnMapping(null);
    setFileName("");
    setShowMappingModal(false);
    setError("");
  }

  // Danh sách dòng đã parse & validate dựa trên column mapping
  const parsedRows: ParsedProductRow[] = useMemo(() => {
    if (!parsedSheet || !columnMapping) return [];

    const existingSkus = new Set(
      products.map((p) => p.sku.trim().toLowerCase()),
    );
    const seenFileSkus = new Set<string>();

    return parsedSheet.rows.map((row, index) => {
      const getValue = (fieldKey: string) => {
        const header = columnMapping[fieldKey];
        return header ? row[header] ?? "" : "";
      };

      const sku = getValue("sku").trim();
      const name = getValue("name").trim();
      const businessVertical = getValue("businessVertical").trim() || null;
      const category = getValue("category").trim() || null;
      const subcategory = getValue("subcategory").trim() || null;
      const barcode = getValue("barcode").trim() || null;
      const baseUnit = getValue("baseUnit").trim() || "cái";
      const salePriceResult = parseImportNumber(
        getValue("salePrice"),
        "Giá bán",
        { integer: true },
      );
      const openingCostResult = parseImportNumber(
        getValue("openingCost"),
        "Giá vốn đầu kỳ",
        { integer: true },
      );
      const openingQtyResult = parseImportNumber(
        getValue("openingQty"),
        "Tồn đầu kỳ",
      );
      const minStockQtyResult = parseImportNumber(
        getValue("minStockQty"),
        "Tồn tối thiểu",
      );

      const salePrice = salePriceResult.value;
      const openingCost = openingCostResult.value;
      const openingQty = openingQtyResult.value;
      const minStockQty = minStockQtyResult.value;
      const trackExpiry = parseBoolean(getValue("trackExpiry"));
      const openingLotCode = getValue("openingLotCode").trim() || null;
      const openingExpiryDate = getValue("openingExpiryDate").trim() || null;

      let units: unknown[] = [];
      const unitsRaw = getValue("unitsJson").trim();
      const errors: string[] = [
        salePriceResult.error,
        openingCostResult.error,
        openingQtyResult.error,
        minStockQtyResult.error,
      ].filter((value): value is string => Boolean(value));

      if (!sku) errors.push("Thiếu mã SKU");
      if (!name) errors.push("Thiếu tên sản phẩm");

      if (sku) {
        const skuLower = sku.toLowerCase();
        if (seenFileSkus.has(skuLower)) {
          errors.push(`Mã SKU "${sku}" bị trùng lặp trong file`);
        } else {
          seenFileSkus.add(skuLower);
        }

        if (existingSkus.has(skuLower)) {
          errors.push(`Mã SKU "${sku}" đã tồn tại trên hệ thống`);
        }
      }

      if (trackExpiry && openingQty > 0 && !openingExpiryDate) {
        errors.push("Sản phẩm theo dõi HSD có tồn đầu cần nhập hạn sử dụng");
      }

      if (unitsRaw) {
        try {
          const parsed = JSON.parse(unitsRaw);
          if (!Array.isArray(parsed)) {
            errors.push("Đơn vị quy đổi (unitsJson) phải là một mảng JSON");
          } else {
            const normalizedUnits: Array<{
              name: string;
              multiplier: number;
              barcode: string | null;
              salePrice: number | null;
            }> = [];

            parsed.forEach((candidate, unitIndex) => {
              if (
                !candidate ||
                typeof candidate !== "object" ||
                Array.isArray(candidate)
              ) {
                errors.push(
                  `Đơn vị quy đổi #${unitIndex + 1} phải là một object JSON`,
                );
                return;
              }

              const item = candidate as Record<string, unknown>;
              const unitName = String(item.name ?? "").trim();
              const multiplier = Number(item.multiplier);
              const barcodeValue = String(item.barcode ?? "").trim();
              const salePriceValue =
                item.salePrice === undefined ||
                item.salePrice === null ||
                String(item.salePrice).trim() === ""
                  ? null
                  : Number(item.salePrice);

              if (!unitName) {
                errors.push(
                  `Đơn vị quy đổi #${unitIndex + 1} thiếu tên đơn vị`,
                );
              }
              if (!Number.isFinite(multiplier) || multiplier <= 0) {
                errors.push(
                  `Đơn vị quy đổi #${unitIndex + 1} có hệ số không hợp lệ`,
                );
              }
              if (
                salePriceValue !== null &&
                (!Number.isSafeInteger(salePriceValue) || salePriceValue < 0)
              ) {
                errors.push(
                  `Đơn vị quy đổi #${unitIndex + 1} có giá bán không hợp lệ`,
                );
              }

              if (
                unitName &&
                Number.isFinite(multiplier) &&
                multiplier > 0 &&
                (salePriceValue === null ||
                  (Number.isSafeInteger(salePriceValue) && salePriceValue >= 0))
              ) {
                normalizedUnits.push({
                  name: unitName,
                  multiplier,
                  barcode: barcodeValue || null,
                  salePrice: salePriceValue,
                });
              }
            });

            const normalizedNames = normalizedUnits.map((unit) =>
              unit.name.toLocaleLowerCase("vi"),
            );
            if (new Set(normalizedNames).size !== normalizedNames.length) {
              errors.push("Tên đơn vị quy đổi không được trùng nhau");
            }

            units = normalizedUnits;
          }
        } catch {
          errors.push("Cấu trúc unitsJson không đúng định dạng JSON");
        }
      }

      return {
        rowIndex: index + 2,
        raw: row,
        sku,
        name,
        businessVertical,
        category,
        subcategory,
        barcode,
        baseUnit,
        salePrice,
        openingCost,
        openingQty,
        minStockQty,
        trackExpiry,
        openingLotCode,
        openingExpiryDate,
        units,
        errors,
      };
    });
  }, [parsedSheet, columnMapping, products]);

  const validRows = useMemo(() => parsedRows.filter((r) => r.errors.length === 0), [parsedRows]);
  const errorRows = useMemo(() => parsedRows.filter((r) => r.errors.length > 0), [parsedRows]);
  const mappedFieldCount = useMemo(
    () => Object.values(columnMapping ?? {}).filter(Boolean).length,
    [columnMapping],
  );
  const importStep = !parsedSheet ? 1 : !columnMapping ? 2 : errorRows.length > 0 ? 3 : 4;

  // Nhập sản phẩm theo semantics all-or-nothing.
  // Online: 1 request -> 1 transaction backend. Demo: 1 Dexie transaction.
  async function submitImport() {
    if (parsedRows.length === 0) {
      setError("Không có dữ liệu sản phẩm để nhập.");
      return;
    }

    if (errorRows.length > 0) {
      setError(
        `Còn ${errorRows.length} dòng lỗi. BLed không nhập một phần file; hãy sửa toàn bộ lỗi trước khi tiếp tục.`,
      );
      return;
    }

    if (onlineRequired) {
      setError("Nhập sản phẩm cần kết nối mạng để lưu đồng bộ dữ liệu.");
      return;
    }

    const productsPayload = validRows.map((row) => ({
      name: row.name,
      sku: row.sku,
      barcode: row.barcode,
      businessVertical: row.businessVertical,
      category: row.category,
      subcategory: row.subcategory,
      baseUnit: row.baseUnit,
      salePrice: row.salePrice,
      openingCost: row.openingCost,
      openingQty: row.openingQty,
      minStockQty: row.minStockQty,
      trackExpiry: row.trackExpiry,
      openingLotCode: row.openingLotCode,
      openingExpiryDate: row.openingExpiryDate,
      units: (row.units as Array<Record<string, unknown>>).map((unit) => ({
        name: String(unit.name ?? "").trim(),
        multiplier: Number(unit.multiplier),
        barcode: unit.barcode ? String(unit.barcode).trim() : null,
        salePrice:
          unit.salePrice === undefined || unit.salePrice === null || unit.salePrice === ""
            ? null
            : Math.round(Number(unit.salePrice)),
      })),
    }));

    setBusy(true);
    setError("");
    setImportProgress({ current: 0, total: productsPayload.length });

    try {
      if (auth.demo) {
        const localProducts: CachedProduct[] = productsPayload.map((row) => ({
          id: crypto.randomUUID(),
          name: row.name,
          sku: row.sku,
          barcode: row.barcode,
          businessVertical: row.businessVertical,
          category: row.category,
          subcategory: row.subcategory,
          baseUnit: row.baseUnit,
          salePrice: row.salePrice,
          avgCost: row.openingCost,
          stockQty: row.openingQty,
          minStockQty: row.minStockQty,
          trackExpiry: row.trackExpiry,
          nextExpiryDate: row.openingExpiryDate
            ? `${row.openingExpiryDate}T00:00:00.000Z`
            : null,
          units: row.units.map((unit) => ({
            id: crypto.randomUUID(),
            name: unit.name,
            multiplier: unit.multiplier,
            barcode: unit.barcode,
            salePrice: unit.salePrice,
          })),
        }));

        await db.transaction("rw", db.products, async () => {
          await db.products.bulkAdd(localProducts);
        });
      } else {
        await api("/imports/batch-products", {
          method: "POST",
          body: JSON.stringify({ products: productsPayload }),
        });
        await refreshFromServer();
      }

      setImportProgress({
        current: productsPayload.length,
        total: productsPayload.length,
      });
      navigate("/products", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? `Không thể nhập danh mục. Không có dữ liệu nào trong batch được ghi: ${err.message}`
          : "Không thể nhập danh mục sản phẩm.",
      );
    } finally {
      setBusy(false);
      setImportProgress(null);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Thêm sản phẩm"
        description="Quản lý Product Master/SKU. Tạo sản phẩm không làm phát sinh tồn kho; số lượng chỉ thay đổi qua nghiệp vụ Kho."
        back
        action={
          <div className="header-actions">
            <button
              type="button"
              className={`btn ${mode === "manual" ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setMode("manual")}
            >
              <PackagePlus size={18} /> Nhập thủ công
            </button>
            <button
              type="button"
              className={`btn ${mode === "import" ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setMode("import")}
            >
              <FileSpreadsheet size={18} /> Nhập từ file CSV / Excel
            </button>
          </div>
        }
      />

      {error ? (
        <InlineNotice tone="danger" title={error} />
      ) : null}

      {onlineRequired ? (
        <InlineNotice tone="warning" title="Đang ngoại tuyến">
          Tạo mã hàng mới cần kết nối để tránh trùng SKU/barcode giữa các thiết bị.
        </InlineNotice>
      ) : null}

      {/* MODAL ÁNH XẠ CỘT */}
      {showMappingModal && parsedSheet ? (
        <ColumnMappingModal
          headers={parsedSheet.headers}
          sampleRows={parsedSheet.rows}
          fields={PRODUCT_FIELDS}
          onConfirm={(mapping) => {
            setColumnMapping(mapping);
            setShowMappingModal(false);
          }}
          onCancel={() => setShowMappingModal(false)}
        />
      ) : null}

      {/* CHẾ ĐỘ 1: NHẬP THỦ CÔNG */}
      {mode === "manual" ? (
        <form className="card product-form" onSubmit={submitManual}>
          <div className="form-section-title">
            <PackagePlus size={20} />
            <h2>Thông tin SKU / sản phẩm</h2>
          </div>

          <label className="form-span">
            Tên sản phẩm
            <input
              value={form.name}
              onChange={(event) => patch("name", event.target.value)}
              placeholder="Ví dụ: Coca Cola 330ml"
              required
            />
          </label>

          <div className="form-grid">
            <label>
              Ngành kinh doanh
              <input
                value={form.businessVertical}
                onChange={(event) => patch("businessVertical", event.target.value)}
                placeholder="Ví dụ: Tạp hóa & Bách hóa"
              />
            </label>
            <label>
              Danh mục
              <input
                value={form.category}
                onChange={(event) => patch("category", event.target.value)}
                placeholder="Ví dụ: Đồ uống"
              />
            </label>
            <label>
              Danh mục con
              <input
                value={form.subcategory}
                onChange={(event) => patch("subcategory", event.target.value)}
                placeholder="Ví dụ: Nước ngọt có ga"
              />
            </label>
          </div>

          <div className="form-grid">
            <label>
              Mã SKU
              <input
                value={form.sku}
                onChange={(event) =>
                  patch("sku", event.target.value.toUpperCase())
                }
                placeholder="COCA330"
                required
              />
            </label>

            <label>
              Mã vạch đơn vị cơ bản
              <div className="input-with-icon">
                <Barcode size={19} />
                <input
                  inputMode="numeric"
                  value={form.barcode}
                  onChange={(event) => patch("barcode", event.target.value)}
                  placeholder="893..."
                />
              </div>
            </label>

            <label>
              Đơn vị cơ bản
              <input
                value={form.baseUnit}
                onChange={(event) => patch("baseUnit", event.target.value)}
                placeholder="lon / chai / gói"
              />
            </label>

            <label>
              Giá bán / {form.baseUnit || "đơn vị"}
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="100"
                value={form.salePrice || ""}
                onChange={(event) =>
                  patch("salePrice", Number(event.target.value) || 0)
                }
              />
            </label>
          </div>

          <div className="form-section-title form-section-split">
            <div>
              <h2>Quy cách bán / nhập</h2>
              <small>Ví dụ 1 thùng = 24 lon</small>
            </div>
            <button
              className="btn btn-secondary compact"
              type="button"
              onClick={addUnit}
            >
              <Plus size={17} /> Thêm quy cách
            </button>
          </div>

          {form.units.length === 0 ? (
            <InlineNotice tone="info" title="Không bắt buộc">
              Nếu chỉ bán theo {form.baseUnit || "đơn vị cơ bản"}, có thể bỏ qua phần này.
            </InlineNotice>
          ) : (
            <div className="unit-editor-list">
              {form.units.map((unit) => (
                <div className="unit-editor-row" key={unit.id}>
                  <div className="form-grid">
                    <label>
                      Tên quy cách
                      <input
                        value={unit.name}
                        onChange={(event) =>
                          patchUnit(unit.id, { name: event.target.value })
                        }
                        placeholder="thùng / lốc"
                      />
                    </label>

                    <label>
                      1 quy cách = bao nhiêu {form.baseUnit || "đơn vị"}
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={unit.multiplier || ""}
                        onChange={(event) =>
                          patchUnit(unit.id, {
                            multiplier: Number(event.target.value) || 0,
                          })
                        }
                      />
                    </label>

                    <label>
                      Mã vạch riêng
                      <input
                        value={unit.barcode}
                        onChange={(event) =>
                          patchUnit(unit.id, { barcode: event.target.value })
                        }
                      />
                    </label>

                    <label>
                      Giá bán quy cách
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={unit.salePrice || ""}
                        onChange={(event) =>
                          patchUnit(unit.id, {
                            salePrice: Number(event.target.value) || 0,
                          })
                        }
                        placeholder="Để trống = tự nhân giá cơ bản"
                      />
                    </label>
                  </div>

                  <button
                    className="btn btn-destructive compact"
                    type="button"
                    onClick={() => removeUnit(unit.id)}
                  >
                    <Trash2 size={16} /> Xóa quy cách
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="form-section-title">
            <h2>Thiết lập quản lý</h2>
          </div>

          <div className="form-grid">
            <label>
              Mức cảnh báo sắp hết
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.minStockQty || ""}
                onChange={(event) =>
                  patch("minStockQty", Number(event.target.value) || 0)
                }
              />
            </label>

            <label className="toggle-field">
              <input
                type="checkbox"
                checked={form.trackExpiry}
                onChange={(event) => patch("trackExpiry", event.target.checked)}
              />
              <span>Theo dõi lô / hạn sử dụng</span>
            </label>
          </div>

          <InlineNotice tone="info" title="Product Master không ghi tồn kho">
            SKU mới luôn bắt đầu với tồn = 0 và giá vốn = 0. Hãy dùng màn Nhập hàng
            hoặc Điều chỉnh tồn để phát sinh số lượng, giá vốn, mã lô và hạn sử dụng.
          </InlineNotice>

          <button
            className="btn btn-primary btn-lg"
            disabled={busy || onlineRequired}
          >
            <Save size={20} /> {busy ? "Đang lưu..." : "Lưu sản phẩm"}
          </button>
        </form>
      ) : (
        /* CHẾ ĐỘ 2: NHẬP SẢN PHẨM TỪ FILE CSV / EXCEL */
        <div className="page-stack import-workspace">
          <Card className="import-progress-card">
            <div className="import-steps" aria-label="Tiến trình nhập sản phẩm">
              {[
                [1, "Chọn tệp"],
                [2, "Đối ánh cột"],
                [3, "Kiểm tra"],
                [4, "Sẵn sàng nhập"],
              ].map(([step, label]) => (
                <div
                  key={step}
                  className={`import-step ${importStep === step ? "active" : ""} ${importStep > Number(step) ? "done" : ""}`}
                >
                  <span>{importStep > Number(step) ? "✓" : step}</span>
                  <strong>{label}</strong>
                </div>
              ))}
            </div>
          </Card>

          <Card className="form-stack import-source-card">
            <div className="form-section-title form-section-split">
              <div>
                <h2>Chọn tệp danh mục sản phẩm</h2>
                <small>CSV/Excel · tối đa 500 dòng/lần · tự nhận diện tiêu đề Việt/Anh</small>
              </div>
              <div className="import-template-actions">
                <button
                  type="button"
                  className="btn btn-secondary compact"
                  onClick={downloadCsvTemplate}
                >
                  <Download size={16} /> CSV mẫu
                </button>
                <button
                  type="button"
                  className="btn btn-secondary compact"
                  onClick={downloadExcelTemplate}
                >
                  <FileSpreadsheet size={16} /> Excel mẫu
                </button>
              </div>
            </div>

            <div
              className={`import-dropzone ${dragActive ? "drag-active" : ""} ${parsedSheet ? "has-file" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                if (event.currentTarget === event.target) setDragActive(false);
              }}
              onDrop={(event) => void handleFileDrop(event)}
            >
              <Upload size={34} />
              <div>
                <h3>{dragActive ? "Thả tệp để đọc dữ liệu" : parsedSheet ? "Chọn tệp khác hoặc kéo thả để thay thế" : "Kéo thả CSV/Excel vào đây"}</h3>
                <p>Hoặc nhấp để chọn từ máy tính. Hệ thống chỉ đọc tệp trước, chưa ghi dữ liệu.</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.xlsx,.xls"
                hidden
                onChange={(event) => void handleFileChange(event)}
              />
            </div>

            {fileName ? (
              <div className="import-file-summary">
                <div className="import-file-icon">
                  <FileText size={20} />
                </div>
                <div className="import-file-main">
                  <strong>{fileName}</strong>
                  {parsedSheet ? (
                    <small>
                      Sheet: {parsedSheet.sheetName} · {parsedSheet.totalRows} dòng · {parsedSheet.headers.length} cột
                      {columnMapping ? ` · ${mappedFieldCount} trường đã đối ánh` : ""}
                    </small>
                  ) : (
                    <small>Đang đọc tệp...</small>
                  )}
                </div>
                <div className="import-file-actions">
                  {parsedSheet ? (
                    <button
                      type="button"
                      className="btn btn-secondary compact"
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowMappingModal(true);
                      }}
                    >
                      <Columns size={16} /> Đối ánh cột
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-secondary compact"
                    onClick={clearImportFile}
                  >
                    Chọn lại
                  </button>
                </div>
              </div>
            ) : null}

            <div className="import-guardrail">
              <strong>Quy tắc dữ liệu:</strong>
              <span>Khai báo SKU không làm tăng tồn kho. Sau khi tạo danh mục, dùng Kho → Nhập hàng/Import phiếu nhập để phát sinh số lượng và giá vốn.</span>
            </div>
          </Card>

          {/* BẢNG KẾT QUẢ PARSE & XÁC NHẬN SẢN PHẨM */}
          {parsedSheet && columnMapping ? (
            <Card className="form-stack">
              <div className="form-section-title form-section-split">
                <div>
                  <h2>Xem trước Product Master / SKU</h2>
                  <small>Kiểm tra trước khi ghi. BLedger chỉ nhập khi toàn bộ file hợp lệ.</small>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <StatusBadge tone={validRows.length > 0 ? "success" : "neutral"}>
                    {validRows.length} hợp lệ
                  </StatusBadge>
                  {errorRows.length > 0 ? (
                    <StatusBadge tone="danger">
                      {errorRows.length} có lỗi
                    </StatusBadge>
                  ) : null}
                </div>
              </div>

              {errorRows.length > 0 ? (
                <InlineNotice tone="warning" title={`Còn ${errorRows.length} dòng lỗi — chưa thể nhập`}>
                  BLedger dùng cơ chế all-or-nothing để tránh danh mục bị nhập dở dang. Sửa file hoặc điều chỉnh đối ánh cột rồi kiểm tra lại.
                </InlineNotice>
              ) : (
                <InlineNotice tone="success" title="Dữ liệu sẵn sàng">
                  Toàn bộ {validRows.length} dòng đã vượt kiểm tra. Bạn có thể thực hiện nhập danh mục.
                </InlineNotice>
              )}

              {importProgress ? (
                <div style={{ padding: "12px", backgroundColor: "#eff6ff", borderRadius: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#1d4ed8", fontWeight: 600 }}>
                    <RefreshCw className="animate-spin" size={18} />
                    Đang lưu sản phẩm {importProgress.current} / {importProgress.total}...
                  </div>
                </div>
              ) : null}

              {/* BẢNG SẢN PHẨM HOÀN CHỈNH */}
              <div className="import-table-wrap">
                <table className="data-table import-preview-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>Dòng</th>
                      <th>Mã SKU</th>
                      <th>Tên sản phẩm</th>
                      <th>Danh mục</th>
                      <th>Mã vạch</th>
                      <th>ĐVT</th>
                      <th style={{ textAlign: "right" }}>Giá bán</th>
                      <th style={{ textAlign: "right" }}>Tồn tối thiểu</th>
                      <th>Theo dõi HSD</th>
                      <th>Trạng thái</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((row) => (
                      <tr
                        key={row.rowIndex}
                        style={{
                          backgroundColor: row.errors.length > 0 ? "#fef2f2" : undefined,
                        }}
                      >
                        <td>{row.rowIndex}</td>
                        <td style={{ fontWeight: 600 }}>{row.sku || "—"}</td>
                        <td>{row.name || "—"}</td>
                        <td>
                          <div className="taxonomy-cell">
                            <span>{row.category || "—"}</span>
                            {row.subcategory ? <small>{row.subcategory}</small> : null}
                          </div>
                        </td>
                        <td>{row.barcode || "—"}</td>
                        <td>{row.baseUnit}</td>
                        <td style={{ textAlign: "right" }}>{row.salePrice.toLocaleString("vi-VN")} đ</td>
                        <td style={{ textAlign: "right" }}>{row.minStockQty}</td>
                        <td>{row.trackExpiry ? "Có" : "Không"}</td>
                        <td>
                          {row.errors.length === 0 ? (
                            <span style={{ color: "#16a34a", display: "flex", alignItems: "center", gap: "4px", fontWeight: 500 }}>
                              <CheckCircle2 size={16} /> Hợp lệ
                            </span>
                          ) : (
                            <div style={{ color: "#dc2626", fontSize: "0.775rem" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "4px", fontWeight: 600 }}>
                                <AlertTriangle size={15} /> Lỗi
                              </div>
                              {row.errors.map((e, idx) => (
                                <div key={idx}>• {e}</div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="import-confirm-bar">
                <div>
                  <strong>{errorRows.length === 0 ? `${validRows.length} SKU sẵn sàng` : `${errorRows.length} dòng cần xử lý`}</strong>
                  <small>{errorRows.length === 0 ? "Không phát sinh tồn kho ở bước này." : "Nút nhập được khóa đến khi file hợp lệ hoàn toàn."}</small>
                </div>
                <div className="import-confirm-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={clearImportFile}
                  >
                    Chọn tệp khác
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={busy || validRows.length === 0 || errorRows.length > 0 || onlineRequired}
                    onClick={submitImport}
                  >
                    <Save size={20} />
                    {busy ? "Đang lưu..." : `Nhập ${validRows.length} sản phẩm`}
                  </button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
